import { LicensedConfigurationResponse } from "@common/types/edge-client-configuration-types";
import { Button } from "@components/atoms/buttons/button/button";
import { SubHeader } from "@components/atoms/sub-header/sub-header";
import { SubTitle } from "@components/atoms/sub-title/sub-title";
import { ContainerPane } from "@components/organisms/container-pane/container-pane";
import { ContainerPanel } from "@components/organisms/container-panel/container-panel";
import { ErrorPane } from "@components/organisms/error-pane/error-pane";
import downloadLicensedConfiguration from "@src/cloud-connect/api/use-cases/download-licensed-configuration";
import cn from "classnames";
import React from "react";
import styles from "./configuration-manager-page.module.scss";
import { DeleteConfigurationButton } from "./delete-configuration-button";
import { getIpPort } from "./helpers";
import { useI18N } from "@src/language/language-context";

export const LicensedConfigurationTable = ({
  data,
  isError,
  onCreateConfiguration,
}: {
  data: LicensedConfigurationResponse;
  isError: boolean;
  onCreateConfiguration: (id: string) => void;
}) => {
  const i18n = useI18N();
  if (isError) {
    return (
      <ErrorPane
        titleText={i18n("pages.configurationManager.genericErrorTitle")}
        hintTitleText={i18n("pages.configurationManager.genericErrorHint")}
        buttonText={i18n("pages.configurationManager.button")}
      />
    );
  }

  const headerStyles = cn(
    styles["configuration-manager-page__grid-cell"],
    styles["configuration-manager-page__grid-cell-header"],
  );

  const userLicensesNote = `Used ${data.used_licences_count} of ${data.total_licences_count} licenses`;

  return (
    <ContainerPanel>
      <SubHeader>
        <SubTitle text={i18n("pages.configurationManager.licensesHeader")} />
      </SubHeader>
      <ContainerPane>
        <table>
          <thead>
            <tr className={styles["configuration-manager-page__header"]}>
              <th className={headerStyles}>
                {i18n("pages.configurationManager.licenseId")}
              </th>
              <th className={headerStyles}>
                {i18n("pages.configurationManager.ipPort")}
              </th>
              <th className={headerStyles}>
                {i18n("pages.configurationManager.configurationFile")}
              </th>
              <th className={headerStyles}>
                {i18n("pages.configurationManager.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.licensed_configurations.map((licensedConfiguration) => {
              return (
                <tr key={licensedConfiguration.config_id}>
                  <td>{licensedConfiguration.license.id}</td>
                  <td>{getIpPort(licensedConfiguration.data_source_dsn)}</td>
                  <td>
                    <Button
                      text={i18n("pages.configurationManager.download")}
                      fun={() =>
                        downloadLicensedConfiguration(
                          licensedConfiguration.configuration_download_url,
                          licensedConfiguration.license.id,
                        )
                      }
                    />
                  </td>
                  <td>
                    <DeleteConfigurationButton
                      id={licensedConfiguration.config_id}
                    />
                  </td>
                </tr>
              );
            })}
            {data?.available_licences.map((availableLicense) => {
              return (
                <tr key={availableLicense.id}>
                  <td>{availableLicense.id}</td>
                  <td></td>
                  <td></td>
                  <td>
                    <Button
                      id={availableLicense.id}
                      text={i18n("pages.configurationManager.create")}
                      variant="secondary"
                      fun={() => onCreateConfiguration(availableLicense.id)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td
                colSpan={4}
                className={
                  styles["configuration-manager-page__grid-cell-footer"]
                }
              >
                {userLicensesNote}
              </td>
            </tr>
          </tfoot>
        </table>
      </ContainerPane>
    </ContainerPanel>
  );
};
